/**
 * Collection of Google JS helpers/utils.
 */
import {t} from "@lingui/core/macro";

import {Errors, KAError} from "@khan/logging";

import {isIGoogleAPI, type IGoogleAPI} from "./google-api.ts";
import {
    GoogleClient,
    isGoogleIdentityServices,
    type ClientConfigError,
    type CodeClient,
    type CodeResponse,
    type GoogleClientInfo,
    type GoogleIdentityServices,
    type TokenClient,
    type TokenResponse,
} from "./types.ts";

// sync-start:google-auth-client-id 1714668476 https://github.com/Khan/webapp/blob/master/services/users/auth/google/exchange.go
// Client ID for the Google API used for Sign In.
const CLIENT_ID =
    "124072386181-eogtmmv0qose5ovudl946d83miv1ia89.apps.googleusercontent.com";
// sync-end:google-auth-client-id

/**
 * A collection of Google client libraries that we support.
 */
const GoogleLibraries: Record<string, GoogleClientInfo> = {
    // NOTE: The GAPI client is deprecated in favor of the GSI client for
    // authentication and authorization purposes, but it will be still used for
    // other Google APIs (like Google Classroom).
    GAPI: {
        script: "https://apis.google.com/js/api:client.js",
        module: "gapi",
    },
    // The Google Identity Services (GSI) client is used for authentication and
    // authorization.
    GSI: {
        script: "https://accounts.google.com/gsi/client",
        module: "google",
    },
};

/**
 * Returns the Google library info for a given client.
 * @param {GoogleClient} client The Google client to get the info for.
 * @returns {GoogleClientInfo} The Google library info.
 */
function getGoogleClient(
    client: (typeof GoogleClient)[keyof typeof GoogleClient],
): GoogleClientInfo {
    switch (client) {
        case GoogleClient.GAPI:
            return GoogleLibraries.GAPI;
        default:
            return GoogleLibraries.GSI;
    }
}

/**
 * Loads a given Google client API.
 *
 * @param {GoogleClient} client The Google JS client to load.
 * @returns {Promise<GoogleIdentityServices | IGoogleAPI>} The loaded client.
 */
const loadGoogleClient = (
    client: (typeof GoogleClient)[keyof typeof GoogleClient] = GoogleClient.GAPI,
): Promise<GoogleIdentityServices | IGoogleAPI> => {
    const selectedLibrary: GoogleClientInfo = getGoogleClient(client);

    if (globalThis[selectedLibrary.module]) {
        return Promise.resolve(globalThis[selectedLibrary.module]);
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = selectedLibrary.script;
        script.onload = () => {
            resolve(globalThis[selectedLibrary.module]);
        };
        script.onerror = (error: unknown) => {
            reject(error);
        };
        document.body?.appendChild(script);
    });
};

/**
 * Loads a given Google GSI client.
 *
 * @returns {Promise<GoogleIdentityServices>} The loaded client.
 */
export const loadGoogleGSIClient = (): Promise<GoogleIdentityServices> => {
    return loadGoogleClient(GoogleClient.GSI).then((client) => {
        if (!isGoogleIdentityServices(client)) {
            return Promise.reject(
                new Error(
                    "received an unexpected Google API client type, expected GoogleIdentityServices",
                ),
            );
        }
        return client;
    });
};

/**
 * Loads a given Google API client.
 *
 * @returns {Promise<IGoogleAPI>} The loaded client.
 */
export const loadGoogleGAPIClient = (): Promise<IGoogleAPI> => {
    return loadGoogleClient(GoogleClient.GAPI).then((client) => {
        if (!isIGoogleAPI(client)) {
            return Promise.reject(
                new Error(
                    "received an unexpected Google API client type, expected IGoogleAPI",
                ),
            );
        }
        return client;
    });
};

type Timestamp = number;

/**
 * A cache of the access tokens for a given sets of scopes.
 *
 * The key is a string containing the scope or scopes separated by spaces.
 *
 * NOTE:
 * - This cache doesn't across page loads.
 * - Cache keys can contain the multiple scopes and thus there is potential
 *   some cache keys contain the same scope. It should be just fine to call
 *   `authorizeWithImplicitGrant` for the same scope multiple times.
 */
const accessTokenForScopes: Map<string, [Timestamp, TokenResponse]> = new Map();

/**
 * Authorize the application to access a given set of Google APIs (scopes).
 *
 * When this happens, the token becomes available, and we can use it to
 * determine whether we can try the API calls.
 *
 * @param {string} clientID - The client ID to authorize with.
 * @param {Array<string>} scopes - The scopes to authorize for.
 * @param {boolean} reauthOnExpiredToken - Whether to reauthorize if the access
 * token has expired. This should always be `true` when making use of the
 * access token.
 * @returns {Promise<boolean>} - A promise that resolves if the scopes are
 * already granted or if the token is valid.
 */
export async function authorizeWithImplicitGrant(
    clientID: string,
    scopes: Array<string>,
    reauthOnExpiredToken: boolean = false,
): Promise<TokenResponse | undefined> {
    const google: GoogleIdentityServices = await loadGoogleGSIClient();

    return new Promise((resolve, reject) => {
        const scope = scopes.join(" ");

        // Check if we've already authorized for the given scopes in the current
        // session.
        const value = accessTokenForScopes.get(scope);
        if (value) {
            if (!reauthOnExpiredToken) {
                return resolve(undefined);
            }

            // If the token is expired, we need to reauthorize, but only
            // if the caller requests this behaviour.
            const [timestamp, accessToken] = value;
            if (timestamp + accessToken.expires_in > Date.now()) {
                return resolve(accessToken);
            }
        }

        // https://developers.google.com/identity/oauth2/web/reference/js-reference#google.accounts.oauth2.initTokenClient
        const client: TokenClient = google.accounts.oauth2.initTokenClient({
            client_id: clientID,
            scope,
            prompt: "",
            callback: (token: TokenResponse) => {
                // Token is valid, resolve the promise.
                if (token && token.access_token) {
                    // Check that all scopes were granted
                    if (
                        scopes.length === 0 ||
                        google.accounts.oauth2.hasGrantedAllScopes(
                            token,
                            scopes[0],
                            ...scopes.slice(1),
                        )
                    ) {
                        // Add the scopes that are currently granted to the list of
                        // granted scopes so we can cache them for this session.
                        accessTokenForScopes.set(scope, [Date.now(), token]);
                        return resolve(token);
                    }
                    return reject(
                        new KAError(
                            "Failed to authorize all scopes with Google",
                            Errors.Internal,
                        ),
                    );
                }

                reject(
                    new KAError(
                        "Failed to authorize with Google",
                        Errors.Internal,
                    ),
                );
            },
        });

        // Invoke the immediate mode flow so the user can authorize the app.
        // NOTE: This will only be called by a user action, like a button click.
        client.requestAccessToken();
    });
}

function getMessageByErrorType(errorType: string): string {
    switch (errorType) {
        case "popup_closed":
            return t`You closed the popup window before logging in. If that was a mistake, try logging in again.`;
        case "popup_failed_to_open":
            return t`Your browser blocked a popup window required for logging in. Please change your browser settings to allow popups from khanacademy.org.`;
        case "unknown":
        default:
            return t`Oops. Something went wrong. Please try again.`;
    }
}

/**
 * Authorize the application to access a given set of Google APIs (scopes).
 *
 * This method uses the Code Model flow that returns a code that can be later
 * exchanged for a valid ID token (JWT token).
 * https://developers.google.com/identity/oauth2/web/guides/use-code-model
 *
 * @param {function} handleCodeResponse - A callback function that will be
 * called when the code response is received.
 * @param {function} handleErrorResponse - A callback function that will be
 * called when an error occurs.
 * @returns {Promise<CodeClient>} - A promise that resolves to the authorization
 * code.
 */
export const initCodeClient = (
    handleCodeResponse: (response: CodeResponse) => void,
    handleErrorResponse: (errorMessage: string) => void = () => {},
): Promise<CodeClient> =>
    loadGoogleGSIClient()
        .then((google) =>
            google.accounts.oauth2.initCodeClient({
                client_id: CLIENT_ID,
                scope: "openid email profile",
                callback: handleCodeResponse,
                error_callback: (error: ClientConfigError) => {
                    // Exchange the error code for a human-readable error
                    // message.
                    const errorMessage = getMessageByErrorType(error.type);
                    handleErrorResponse(errorMessage);
                },
            }),
        )
        .catch((error) => {
            throw new KAError(
                `Failed to load Google Auth API: ${error}`,
                Errors.Internal,
            );
        });
